import { inject, Injectable } from '@angular/core';
import { ChatIntent } from '../models/guide.model';
import { ActionRegistryService } from './action-registry.service';
import { FaqRegistryService } from './faq-registry.service';

/** Below this score we return `unclear`/`informational` rather than guess. */
const MATCH_THRESHOLD = 0.34;

/** Common question words that inflate short-phrase overlap scores. */
const STOPWORDS = new Set([
  'a',
  'an',
  'the',
  'is',
  'are',
  'do',
  'does',
  'did',
  'how',
  'what',
  'where',
  'why',
  'when',
  'can',
  'i',
  'my',
  'me',
  'to',
  'of',
  'for',
  'on',
  'in',
  'with',
  'and',
  'or',
  'it',
]);

/**
 * MVP intent matcher — a local, deterministic keyword/phrase matcher over the
 * registry's `triggerPhrases` (tasks first, then FAQs).
 *
 * SEAM: in the backend pass this is replaced by a call to
 * `POST /ai/classify-intent` (OpenAI via the existing `ChatGptService`). The
 * return type ({@link ChatIntent}) is intentionally identical so callers do not
 * change.
 */
@Injectable({ providedIn: 'root' })
export class IntentMatcherService {
  private readonly registry = inject(ActionRegistryService);
  private readonly faqRegistry = inject(FaqRegistryService);

  match(message: string): ChatIntent {
    const normalised = this.normalise(message);
    if (!normalised) {
      return { type: 'unclear', confidence: 0 };
    }

    const messageTokens = new Set(normalised.split(' '));

    // How-to tasks take priority when both could match.
    const bestTask = this.bestMatch(
      normalised,
      messageTokens,
      this.registry.tasks().map((task) => ({
        id: task.id,
        phrases: task.triggerPhrases,
      })),
    );

    if (bestTask && bestTask.score >= MATCH_THRESHOLD) {
      return { type: 'how_to', matchedTaskId: bestTask.id, confidence: bestTask.score };
    }

    const bestFaq = this.bestMatch(
      normalised,
      messageTokens,
      this.faqRegistry.faqs().map((faq) => ({
        id: faq.id,
        phrases: faq.triggerPhrases,
      })),
    );

    if (bestFaq && bestFaq.score >= MATCH_THRESHOLD) {
      return { type: 'faq', matchedFaqId: bestFaq.id, confidence: bestFaq.score };
    }

    // Looks like a question but nothing matched → informational; otherwise unclear.
    const looksLikeQuestion = /^(how|what|where|why|when|can|do|does|is)\b/.test(normalised);
    const bestScore = Math.max(bestTask?.score ?? 0, bestFaq?.score ?? 0);
    return {
      type: looksLikeQuestion ? 'informational' : 'unclear',
      confidence: bestScore,
    };
  }

  private bestMatch(
    normalisedMessage: string,
    messageTokens: Set<string>,
    entries: { id: string; phrases: string[] }[],
  ): { id: string; score: number } | undefined {
    let best: { id: string; score: number } | undefined;

    for (const entry of entries) {
      for (const phrase of entry.phrases) {
        const score = this.scorePhrase(normalisedMessage, messageTokens, phrase);
        if (!best || score > best.score) {
          best = { id: entry.id, score };
        }
      }
    }

    return best;
  }

  /** Fraction of the phrase's meaningful words present in the message (0–1). */
  private scorePhrase(normalisedMessage: string, messageTokens: Set<string>, phrase: string): number {
    const normalisedPhrase = this.normalise(phrase);
    if (!normalisedPhrase) {
      return 0;
    }

    // Exact/substring containment is a strong signal.
    if (normalisedMessage.includes(normalisedPhrase)) {
      return 1;
    }

    const phraseTokens = normalisedPhrase.split(' ');
    const meaningfulTokens = phraseTokens.filter((token) => !STOPWORDS.has(token));
    const tokensToMatch = meaningfulTokens.length > 0 ? meaningfulTokens : phraseTokens;
    const overlap = tokensToMatch.filter((token) => messageTokens.has(token)).length;

    // Short content phrases are noisy via partial overlap — require every
    // meaningful token (e.g. "what is shout" must include "shout").
    if (tokensToMatch.length <= 3 && overlap < tokensToMatch.length) {
      return 0;
    }

    return overlap / tokensToMatch.length;
  }

  private normalise(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
